// src/components/templates/diary/DiaryGoalsView.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Target, Plus, Calendar, TrendingUp, Trophy, Zap, ChevronRight, ChevronDown,
  Clock, CheckCircle, Circle, AlertTriangle, Flame, Award, BarChart3, User,
  Edit2, Trash2, X, Save, Loader2, Play, Pause, Flag, Star, Users, Lock, StopCircle, 
  ArrowRight, RefreshCw, FileText, MessageSquare, Settings, Eye,
  Briefcase, Building2, DollarSign, Heart, Sparkles, Sun, GraduationCap,
  Palette, Globe, MoreVertical, Filter, ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import { Button, Card, Badge, Input, TextArea, Select, Avatar } from './UIComponents';
import {
  GOAL_STATUS, GOAL_CATEGORIES, GOAL_PRIORITY, MEASURE_TYPES,
  TWELVE_WEEK_PERIOD, WEEKLY_RATINGS, TIME_BLOCK_TYPES,
  getCurrentWeekNumber, calculateGoalProgress, getScoreRating, generateWeekDates,
  getDaysRemaining, getNextPeriodStartDate, getPeriodEndDate
} from '../../constants/goalTypes';

// --- CATEGORY ICONS ---
const CATEGORY_ICONS = {
  career: Briefcase, business: Building2, financial: DollarSign, health: Heart,
  relationships: Users, personal: Sparkles, spiritual: Sun, education: GraduationCap,
  creative: Palette, community: Globe
};

// --- PROGRESS RING ---
const ProgressRing = ({ progress, size = 80, strokeWidth = 8, color = 'indigo' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;
  
  const colorClasses = {
    indigo: 'text-indigo-500', green: 'text-green-500', amber: 'text-amber-500',
    red: 'text-red-500', blue: 'text-blue-500', purple: 'text-purple-500'
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          className="text-gray-200"
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className={colorClasses[color] || 'text-indigo-500'}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold text-gray-900">{progress}%</span>
      </div>
    </div>
  );
};

// --- WEEK INDICATOR ---
const WeekIndicator = ({ currentWeek, totalWeeks = 12 }) => {
  return (
    <div className="flex items-center gap-1">
      {[...Array(totalWeeks)].map((_, i) => {
        const weekNum = i + 1;
        const isPast = weekNum < currentWeek;
        const isCurrent = weekNum === currentWeek;
        
        return (
          <div
            key={i}
            className={`w-6 h-2 rounded-full transition-all ${
              isCurrent ? 'bg-indigo-500 scale-y-150' :
              isPast ? 'bg-green-400' : 'bg-gray-200'
            }`}
            title={`Week ${weekNum}`}
          />
        );
      })}
    </div>
  );
};

// --- SCORE BADGE ---
const ScoreBadge = ({ score }) => {
  const rating = getScoreRating(score);
  const colorMap = {
    green: 'bg-green-100 text-green-700 border-green-200',
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    yellow: 'bg-amber-100 text-amber-700 border-amber-200',
    red: 'bg-red-100 text-red-700 border-red-200'
  };
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium border ${colorMap[rating.color]}`}>
      {rating.emoji} {score}%
    </span>
  );
};

// --- GOAL CARD ---
const GoalCard = ({ goal, analytics, currentWeek, onEdit, onDelete, onScore, onView }) => {
  const [expanded, setExpanded] = useState(false);
  const categoryInfo = GOAL_CATEGORIES[goal.category] || GOAL_CATEGORIES.personal;
  const CategoryIcon = CATEGORY_ICONS[goal.category] || Target;
  const priorityInfo = GOAL_PRIORITY[goal.priority?.toUpperCase()] || GOAL_PRIORITY.MEDIUM;
  
  const progress = goal.calculatedProgress || calculateGoalProgress(goal);
  const isOnTrack = analytics?.isOnTrack;
  const trend = analytics?.trend || 'stable';
  
  const TrendIcon = trend === 'improving' ? ArrowUp : trend === 'declining' ? ArrowDown : Minus;
  const trendColor = trend === 'improving' ? 'text-green-500' : trend === 'declining' ? 'text-red-500' : 'text-gray-400';

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-all">
      {/* Header */}
      <div className={`h-2 bg-${categoryInfo.color}-500`} />
      
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className={`p-2 rounded-lg bg-${categoryInfo.color}-100`}>
              <CategoryIcon className={`w-5 h-5 text-${categoryInfo.color}-600`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900 truncate">{goal.title}</h3>
                <Badge color={priorityInfo.color}>{priorityInfo.label}</Badge>
                {goal.status === 'completed' && (
                  <Badge color="green"><Trophy className="w-3 h-3 mr-1" /> Completed</Badge>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{goal.description || goal.vision}</p>
            </div>
          </div>
          
          <ProgressRing progress={progress} size={60} strokeWidth={6} color={categoryInfo.color} />
        </div>

        {/* Lag Measure (Result) */}
        {goal.lagMeasure?.description && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Target className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">{goal.lagMeasure.description}</span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-gray-900">
                  {goal.lagMeasure.currentValue || 0}
                </span>
                <span className="text-gray-400"> / {goal.lagMeasure.targetValue} {goal.lagMeasure.unit}</span>
              </div>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full bg-${categoryInfo.color}-500 transition-all`}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Lead Measures Preview */}
        {goal.leadMeasures?.length > 0 && (
          <div className="mt-4">
            <button 
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <Zap className="w-4 h-4 text-amber-500" />
              <span>{goal.leadMeasures.length} Lead Measure{goal.leadMeasures.length > 1 ? 's' : ''}</span>
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            
            {expanded && (
              <div className="mt-3 space-y-2">
                {goal.leadMeasures.map(measure => {
                  const weekKey = `week${currentWeek}`;
                  const actual = measure.weeklyActuals?.[weekKey] || 0;
                  const measureProgress = measure.targetPerWeek > 0 
                    ? Math.min(Math.round((actual / measure.targetPerWeek) * 100), 100) 
                    : 0;
                  
                  return (
                    <div key={measure.id} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg">
                      <span className="text-sm text-gray-700">{measure.description}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {actual}/{measure.targetPerWeek} {measure.unit}
                        </span>
                        <div className="w-16 bg-gray-200 rounded-full h-1.5">
                          <div 
                            className="h-1.5 rounded-full bg-amber-500"
                            style={{ width: `${measureProgress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Analytics Summary */}
        {analytics && (
          <div className="mt-4 flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <BarChart3 className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Avg:</span>
                <ScoreBadge score={analytics.averageScore || 0} />
              </div>
              <div className={`flex items-center gap-1 ${trendColor}`}>
                <TrendIcon className="w-4 h-4" />
                <span className="capitalize">{trend}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 text-gray-500">
              <Clock className="w-4 h-4" />
              <span>{analytics.weeksRemaining} weeks left</span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => onScore?.(goal)}>
              <CheckCircle className="w-4 h-4 mr-1" /> Score Week {currentWeek}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => onView?.(goal)}>
              <Eye className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => onEdit?.(goal)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <Edit2 className="w-4 h-4" />
            </button>
            <button onClick={() => onDelete?.(goal.id)} className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
};

// --- PERIOD HEADER ---
const PeriodHeader = ({ period, dashboard, onNewPeriod, onEditPeriod }) => {
  if (!period) {
    return (
      <Card className="p-8 text-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-8 h-8 text-indigo-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Start Your 12-Week Year</h2>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          The 12-Week Year system helps you achieve more by creating urgency and focus. 
          Each 12-week period is a fresh opportunity to reach your goals.
        </p>
        <Button onClick={onNewPeriod}>
          <Play className="w-4 h-4 mr-2" /> Start New Period
        </Button>
      </Card>
    );
  }

  const currentWeek = dashboard?.currentWeek || getCurrentWeekNumber(period.startDate);
  const daysRemaining = dashboard?.daysRemaining || getDaysRemaining(period.endDate);
  
  return (
    <Card className="p-6 bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 text-indigo-200 text-sm mb-1">
            <Calendar className="w-4 h-4" />
            <span>{new Date(period.startDate).toLocaleDateString()} — {new Date(period.endDate).toLocaleDateString()}</span>
            <button 
              onClick={() => onEditPeriod?.(period)}
              className="ml-2 p-1 hover:bg-white/20 rounded"
              title="Edit Period"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          </div>
          <h2 className="text-2xl font-bold mb-2">{period.name || '12-Week Sprint'}</h2>
          <p className="text-indigo-100 text-sm max-w-lg">{period.periodVision}</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          {/* Week Progress */}
          <div className="text-center">
            <div className="text-4xl font-bold">{currentWeek}</div>
            <div className="text-indigo-200 text-sm">of 12 weeks</div>
            <div className="mt-2">
              <WeekIndicator currentWeek={currentWeek} />
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{dashboard?.goalsOnTrack || 0}</div>
              <div className="text-xs text-indigo-200">On Track</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{daysRemaining}</div>
              <div className="text-xs text-indigo-200">Days Left</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center col-span-2">
              <div className="text-2xl font-bold">{dashboard?.averagePeriodScore || 0}%</div>
              <div className="text-xs text-indigo-200">Avg Execution Score</div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

// --- GOAL FORM ---
// --- GOAL FORM ---
const GoalForm = ({ goal, period, onSave, onCancel }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    title: goal?.title || '',
    description: goal?.description || '',
    vision: goal?.vision || '',
    category: goal?.category || 'career',
    priority: goal?.priority || 'medium',
    lagMeasure: goal?.lagMeasure || {
      description: '',
      targetValue: 0,
      currentValue: 0,
      startingValue: 0,
      unit: ''
    },
    leadMeasures: goal?.leadMeasures || []
  });
  const [newMeasure, setNewMeasure] = useState({ description: '', targetPerWeek: 0, unit: '' });
  const [saving, setSaving] = useState(false);

  const addLeadMeasure = () => {
    if (!newMeasure.description || !newMeasure.targetPerWeek) return;
    setFormData(prev => ({
      ...prev,
      leadMeasures: [...prev.leadMeasures, {
        id: `lm_${Date.now()}`,
        ...newMeasure,
        weeklyActuals: {},
        isActive: true
      }]
    }));
    setNewMeasure({ description: '', targetPerWeek: 0, unit: '' });
  };

  const removeLeadMeasure = (id) => {
    setFormData(prev => ({
      ...prev,
      leadMeasures: prev.leadMeasures.filter(m => m.id !== id)
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSave?.(formData);
    } finally {
      setSaving(false);
    }
  };

  const isStepValid = () => {
    if (step === 1) return formData.title.trim() && formData.vision.trim();
    if (step === 2) return formData.lagMeasure.description && formData.lagMeasure.targetValue > 0;
    if (step === 3) return formData.leadMeasures.length > 0;
    return true;
  };

  const STEPS = ['Vision', 'Result', 'Actions', 'Review'];

  return (
    <Card className="p-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {STEPS.map((label, i) => (
          <div key={i} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step > i + 1 ? 'bg-green-500 text-white' :
              step === i + 1 ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step > i + 1 ? <CheckCircle className="w-5 h-5" /> : i + 1}
            </div>
            <span className={`ml-2 text-sm ${step === i + 1 ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
              {label}
            </span>
            {i < STEPS.length - 1 && <ChevronRight className="w-5 h-5 text-gray-300 mx-4" />}
          </div>
        ))}
      </div>

      {/* Step 1: Vision */}
      {step === 1 && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">What's your goal?</h3>
            <p className="text-gray-500 text-sm mb-4">Start with a clear, compelling vision of what you want to achieve.</p>
          </div>
          
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Goal Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Launch my online course"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Why does this matter? (Your Vision)</label>
            <textarea
              value={formData.vision}
              onChange={e => setFormData(prev => ({ ...prev, vision: e.target.value }))}
              placeholder="Describe what achieving this goal means to you..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(GOAL_CATEGORIES).slice(0, 6).map(([key, cat]) => {
                  const Icon = CATEGORY_ICONS[key] || Target;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, category: key }))}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        formData.category === key 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mb-1 ${formData.category === key ? 'text-indigo-500' : 'text-gray-400'}`} />
                      <span className="text-sm">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <div className="space-y-2">
                {Object.entries(GOAL_PRIORITY).map(([key, pri]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, priority: key.toLowerCase() }))}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      formData.priority === key.toLowerCase()
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Flag className={`w-4 h-4 ${formData.priority === key.toLowerCase() ? 'text-indigo-500' : 'text-gray-400'}`} />
                      <span className="text-sm font-medium">{pri.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Lag Measure (Result) */}
      {step === 2 && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Define Your Result (Lag Measure)</h3>
            <p className="text-gray-500 text-sm mb-4">
              What specific, measurable outcome will indicate you've achieved this goal?
            </p>
          </div>
          
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <Target className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Lag Measure = Your Result</p>
                <p className="text-sm text-blue-700">This is the outcome you're working toward. It's "lagging" because it only changes after you take action.</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">What result are you measuring?</label>
            <input
              type="text"
              value={formData.lagMeasure.description}
              onChange={e => setFormData(prev => ({ 
                ...prev, 
                lagMeasure: { ...prev.lagMeasure, description: e.target.value }
              }))}
              placeholder="e.g., Course revenue, Weight lost, Books read"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Starting Value</label>
              <input
                type="number"
                value={formData.lagMeasure.startingValue}
                onChange={e => setFormData(prev => ({ 
                  ...prev, 
                  lagMeasure: { ...prev.lagMeasure, startingValue: Number(e.target.value), currentValue: Number(e.target.value) }
                }))}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Target Value</label>
              <input
                type="number"
                value={formData.lagMeasure.targetValue}
                onChange={e => setFormData(prev => ({ 
                  ...prev, 
                  lagMeasure: { ...prev.lagMeasure, targetValue: Number(e.target.value) }
                }))}
                placeholder="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Unit</label>
              <input
                type="text"
                value={formData.lagMeasure.unit}
                onChange={e => setFormData(prev => ({ 
                  ...prev, 
                  lagMeasure: { ...prev.lagMeasure, unit: e.target.value }
                }))}
                placeholder="kg, $, books, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Lead Measures (Actions) */}
      {step === 3 && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Define Your Actions (Lead Measures)</h3>
            <p className="text-gray-500 text-sm mb-4">
              What weekly actions will drive your result? These are activities you control directly.
            </p>
          </div>
          
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900">Lead Measures = Your Actions</p>
                <p className="text-sm text-amber-700">These are the activities that LEAD to results. They're predictive and influenceable.</p>
              </div>
            </div>
          </div>

          {/* Existing Lead Measures */}
          {formData.leadMeasures.length > 0 && (
            <div className="space-y-2">
              {formData.leadMeasures.map(measure => (
                <div key={measure.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium">{measure.description}</span>
                    <span className="text-gray-500 ml-2">
                      ({measure.targetPerWeek} {measure.unit}/week)
                    </span>
                  </div>
                  <button 
                    type="button"
                    onClick={() => removeLeadMeasure(measure.id)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add New Lead Measure */}
          <div className="p-4 border border-dashed border-gray-300 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2 space-y-1">
                <label className="block text-sm font-medium text-gray-700">Action</label>
                <input
                  type="text"
                  value={newMeasure.description}
                  onChange={e => setNewMeasure(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., Make sales calls"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Target/Week</label>
                <input
                  type="number"
                  value={newMeasure.targetPerWeek || ''}
                  onChange={e => setNewMeasure(prev => ({ ...prev, targetPerWeek: Number(e.target.value) }))}
                  placeholder="20"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Unit</label>
                <input
                  type="text"
                  value={newMeasure.unit}
                  onChange={e => setNewMeasure(prev => ({ ...prev, unit: e.target.value }))}
                  placeholder="calls"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <Button variant="secondary" className="mt-3" onClick={addLeadMeasure}>
              <Plus className="w-4 h-4 mr-2" /> Add Lead Measure
            </Button>
          </div>
          
          <p className="text-sm text-gray-500">
            💡 Tip: 2-3 lead measures is ideal. Too many dilutes focus.
          </p>
        </div>
      )}

      {/* Step 4: Review */}
      {step === 4 && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Review Your Goal</h3>
            <p className="text-gray-500 text-sm mb-4">
              Make sure everything looks right before committing to your 12-week goal.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wide">Goal</label>
              <p className="text-xl font-bold text-gray-900">{formData.title}</p>
            </div>
            
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wide">Vision</label>
              <p className="text-gray-700">{formData.vision}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Category</label>
                <p className="font-medium">{GOAL_CATEGORIES[formData.category]?.label}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Priority</label>
                <p className="font-medium">{GOAL_PRIORITY[formData.priority?.toUpperCase()]?.label}</p>
              </div>
            </div>
            
            <div className="pt-4 border-t">
              <label className="text-xs text-gray-500 uppercase tracking-wide">Result (Lag Measure)</label>
              <p className="font-medium">
                {formData.lagMeasure.description}: {formData.lagMeasure.startingValue} → {formData.lagMeasure.targetValue} {formData.lagMeasure.unit}
              </p>
            </div>
            
            <div className="pt-4 border-t">
              <label className="text-xs text-gray-500 uppercase tracking-wide">Weekly Actions (Lead Measures)</label>
              <ul className="mt-2 space-y-1">
                {formData.leadMeasures.map(m => (
                  <li key={m.id} className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />
                    <span>{m.description} — {m.targetPerWeek} {m.unit}/week</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t">
        <Button variant="secondary" onClick={step === 1 ? onCancel : () => setStep(s => s - 1)}>
          {step === 1 ? 'Cancel' : '← Back'}
        </Button>
        
        {step < 4 ? (
          <Button onClick={() => setStep(s => s + 1)} disabled={!isStepValid()}>
            Next →
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {goal ? 'Update Goal' : 'Create Goal'}
          </Button>
        )}
      </div>
    </Card>
  );
};

// --- WEEKLY SCORECARD ---
const WeeklyScorecard = ({ goal, weekNumber, existingScore, onSave, onCancel }) => {
  const [actuals, setActuals] = useState(() => {
    const initial = {};
    goal.leadMeasures?.forEach(m => {
      initial[m.id] = existingScore?.leadMeasureScores?.find(s => s.measureId === m.id)?.actual 
        || m.weeklyActuals?.[`week${weekNumber}`] 
        || 0;
    });
    return initial;
  });
  const [reflection, setReflection] = useState({
    wins: existingScore?.wins || '',
    challenges: existingScore?.challenges || '',
    learnings: existingScore?.learnings || '',
    nextWeekFocus: existingScore?.nextWeekFocus || ''
  });
  const [saving, setSaving] = useState(false);

  const calculatePreviewScore = () => {
    const scores = goal.leadMeasures.map(m => {
      const actual = actuals[m.id] || 0;
      return m.targetPerWeek > 0 ? Math.min((actual / m.targetPerWeek) * 100, 100) : 0;
    });
    return scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
  };

  const previewScore = calculatePreviewScore();
  const rating = getScoreRating(previewScore);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave?.({
        leadMeasureActuals: actuals,
        ...reflection
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold">Week {weekNumber} Scorecard</h3>
          <p className="text-sm text-gray-500">{goal.title}</p>
        </div>
        <div className="text-center">
          <div className={`text-3xl font-bold ${
            rating.color === 'green' ? 'text-green-600' :
            rating.color === 'blue' ? 'text-blue-600' :
            rating.color === 'yellow' ? 'text-amber-600' : 'text-red-600'
          }`}>
            {previewScore}%
          </div>
          <div className="text-sm text-gray-500">{rating.emoji} {rating.label}</div>
        </div>
      </div>

      {/* Lead Measure Tracking */}
      <div className="space-y-4 mb-6">
        <h4 className="font-medium text-gray-700 flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-500" /> Lead Measures
        </h4>
        
        {goal.leadMeasures?.map(measure => {
          const actual = actuals[measure.id] || 0;
          const percentage = measure.targetPerWeek > 0 
            ? Math.min(Math.round((actual / measure.targetPerWeek) * 100), 100) 
            : 0;
          
          return (
            <div key={measure.id} className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{measure.description}</span>
                <span className="text-sm text-gray-500">
                  Target: {measure.targetPerWeek} {measure.unit}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  value={actual}
                  onChange={e => setActuals(prev => ({ ...prev, [measure.id]: Number(e.target.value) }))}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-center font-medium"
                  min="0"
                />
                <div className="flex-1">
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all ${
                        percentage >= 100 ? 'bg-green-500' :
                        percentage >= 75 ? 'bg-blue-500' :
                        percentage >= 50 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
                <span className="text-sm font-medium w-12 text-right">{percentage}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Weekly Reflection */}
      <div className="space-y-4 mb-6">
        <h4 className="font-medium text-gray-700 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-indigo-500" /> Weekly Reflection
        </h4>
        
        <TextArea
          label="🏆 Wins — What went well?"
          value={reflection.wins}
          onChange={e => setReflection(prev => ({ ...prev, wins: e.target.value }))}
          placeholder="Celebrate your progress..."
          rows={2}
        />
        
        <TextArea
          label="⚡ Challenges — What was difficult?"
          value={reflection.challenges}
          onChange={e => setReflection(prev => ({ ...prev, challenges: e.target.value }))}
          placeholder="What obstacles did you face?"
          rows={2}
        />
        
        <TextArea
          label="💡 Learnings — Key insights?"
          value={reflection.learnings}
          onChange={e => setReflection(prev => ({ ...prev, learnings: e.target.value }))}
          placeholder="What did you learn this week?"
          rows={2}
        />
        
        <TextArea
          label="🎯 Next Week Focus"
          value={reflection.nextWeekFocus}
          onChange={e => setReflection(prev => ({ ...prev, nextWeekFocus: e.target.value }))}
          placeholder="What's the priority for next week?"
          rows={2}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Score
        </Button>
      </div>
    </Card>
  );
};

// --- PERIOD EDITOR MODAL ---
const PeriodEditor = ({ period, workspaces = [], onSave, onCancel, onComplete }) => {
  const [formData, setFormData] = useState({
    name: period?.name || '',
    periodVision: period?.periodVision || '',
    startDate: period?.startDate || '',
    workspaceId: period?.workspaceId || null,
    weeklyReviewDay: period?.weeklyReviewDay || 'sunday',
    weeklyReviewTime: period?.weeklyReviewTime || '18:00'
  });
  const [saving, setSaving] = useState(false);

  const periodStartDate = period?.startDate ? new Date(period.startDate) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const hasStarted = periodStartDate && periodStartDate <= today;

  const calculatedEndDate = formData.startDate 
    ? getPeriodEndDate(formData.startDate)
    : period?.endDate;
  
  const handleStartDateChange = (newStartDate) => {
    setFormData(prev => ({
      ...prev,
      startDate: newStartDate
    }));
  };

  // Check if workspace can be changed (only if no goals yet)
  const canChangeWorkspace = !period?.goalIds?.length;

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {
        name: formData.name,
        periodVision: formData.periodVision,
        weeklyReviewDay: formData.weeklyReviewDay,
        weeklyReviewTime: formData.weeklyReviewTime
      };

      if (!hasStarted && formData.startDate !== period?.startDate) {
        updates.startDate = formData.startDate;
        updates.endDate = getPeriodEndDate(formData.startDate);
        updates.weeks = generateWeekDates(formData.startDate);
      }

      // Only update workspace if allowed
      if (canChangeWorkspace && formData.workspaceId !== period?.workspaceId) {
        updates.workspaceId = formData.workspaceId;
        updates.visibility = formData.workspaceId ? 'workspace' : 'private';
      }

      await onSave?.(updates);
    } finally {
      setSaving(false);
    }
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const selectedWorkspace = workspaces.find(w => w.id === formData.workspaceId);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold">Edit 12-Week Period</h3>
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-lg">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <Input
          label="Period Name"
          value={formData.name}
          onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Q1 2025 Sprint"
        />

        <TextArea
          label="Period Vision"
          value={formData.periodVision}
          onChange={e => setFormData(prev => ({ ...prev, periodVision: e.target.value }))}
          placeholder="What does success look like at the end of these 12 weeks?"
          rows={3}
        />

        {/* Workspace Selection */}
        <div>
          <WorkspaceSelector
            value={formData.workspaceId}
            onChange={val => setFormData(prev => ({ ...prev, workspaceId: val }))}
            workspaces={workspaces}
            label="Period Workspace"
            disabled={!canChangeWorkspace}
          />
          {!canChangeWorkspace && (
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Workspace cannot be changed after goals are added
            </p>
          )}
          {selectedWorkspace && (
            <div className="mt-2 p-2 bg-indigo-50 rounded-lg flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-indigo-600" />
              <span className="text-indigo-700">
                Shared with {selectedWorkspace.name} ({selectedWorkspace.memberIds?.length || 0} members)
              </span>
            </div>
          )}
        </div>

        {/* Date Section */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">Period Dates</label>
          
          {hasStarted ? (
            // Period has started - show readonly info
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Period In Progress</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Dates cannot be changed once a period has started.
                  </p>
                  <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-amber-600">Started:</span>
                      <span className="ml-2 font-medium text-amber-900">
                        {new Date(period.startDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-amber-600">Ends:</span>
                      <span className="ml-2 font-medium text-amber-900">
                        {new Date(period.endDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Period hasn't started - allow date editing
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Start Date</label>
                <input
                  type="date"
                  value={formatDateForInput(formData.startDate)}
                  onChange={e => handleStartDateChange(e.target.value)}
                  min={formatDateForInput(new Date())} // Can't start in the past
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">End Date</label>
                <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-700">
                  {calculatedEndDate ? new Date(calculatedEndDate).toLocaleDateString() : '—'}
                </div>
                <p className="text-xs text-gray-500 mt-1">Auto-calculated (12 weeks)</p>
              </div>
            </div>
          )}
        </div>

        {/* Weekly Review Settings */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Weekly Review Day</label>
            <select
              value={formData.weeklyReviewDay}
              onChange={e => setFormData(prev => ({ ...prev, weeklyReviewDay: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="sunday">Sunday</option>
              <option value="monday">Monday</option>
              <option value="friday">Friday</option>
              <option value="saturday">Saturday</option>
            </select>
          </div>
          <Input
            label="Review Time"
            type="time"
            value={formData.weeklyReviewTime}
            onChange={e => setFormData(prev => ({ ...prev, weeklyReviewTime: e.target.value }))}
          />
        </div>

        {/* Period Stats */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{period?.goalIds?.length || 0}</div>
              <div className="text-gray-500">Goals</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {hasStarted ? getCurrentWeekNumber(period.startDate) : 0}
              </div>
              <div className="text-gray-500">Current Week</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 capitalize">{period?.status}</div>
              <div className="text-gray-500">Status</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 pt-4 border-t">
        {hasStarted ? (
          <Button 
            variant="danger" 
            onClick={() => {
              if (window.confirm('End this period early? Goals will remain but period will be marked complete.')) {
                onComplete?.();
              }
            }}
          >
            <StopCircle className="w-4 h-4 mr-2" /> End Period Early
          </Button>
        ) : (
          <Button 
            variant="secondary"
            onClick={() => {
              if (window.confirm('Delete this period? This will unlink all associated goals.')) {
                // Handle delete if needed
              }
            }}
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete Period
          </Button>
        )}
        
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>
    </Card>
  );
};

// --- WORKSPACE SELECTOR ---
// --- WORKSPACE SELECTOR ---
const WorkspaceSelector = ({ 
  value, 
  onChange, 
  workspaces = [], 
  label = "Workspace",
  allowPersonal = true,
  disabled = false 
}) => {
  // Group workspaces by type
  const personalWorkspaces = workspaces.filter(w => w.type === 'personal');
  const orgWorkspaces = workspaces.filter(w => w.type === 'organisation');
  const teamWorkspaces = workspaces.filter(w => w.type === 'team');

  // Helper to get member count
  const getMemberCount = (ws) => ws.memberIds?.length || 0;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value || null)}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        {allowPersonal && (
          <option value="">Personal (Private)</option>
        )}
        
        {personalWorkspaces.length > 0 && (
          <optgroup label="Personal Workspaces">
            {personalWorkspaces.map(ws => (
              <option key={ws.id} value={ws.id}>{ws.name}</option>
            ))}
          </optgroup>
        )}
        
        {orgWorkspaces.length > 0 && (
          <optgroup label="Organisations">
            {orgWorkspaces.map(ws => (
              <option key={ws.id} value={ws.id}>
                {ws.name} ({getMemberCount(ws)} members)
              </option>
            ))}
          </optgroup>
        )}
        
        {teamWorkspaces.length > 0 && (
          <optgroup label="Teams">
            {teamWorkspaces.map(ws => (
              <option key={ws.id} value={ws.id}>
                {ws.name} ({getMemberCount(ws)} members)
              </option>
            ))}
          </optgroup>
        )}
      </select>
      
      {value && (
        <p className="text-xs text-gray-500 mt-1">
          {workspaces.find(w => w.id === value)?.type === 'organisation' || 
           workspaces.find(w => w.id === value)?.type === 'team'
            ? '👥 Visible to all workspace members'
            : '🔒 Only you can see this'}
        </p>
      )}
    </div>
  );
};

// --- WORKSPACE PERIOD SELECTOR ---
const WorkspacePeriodSelector = ({ 
  workspaces = [],
  periodsByWorkspace = {},
  selectedWorkspaceId,
  selectedPeriodId,
  onSelectWorkspace,
  onSelectPeriod,
  onCreatePeriod
}) => {
  const [expanded, setExpanded] = useState(true);

  // Get current selection info
  const currentWorkspace = selectedWorkspaceId 
    ? workspaces.find(w => w.id === selectedWorkspaceId) 
    : null;
  
  const currentPeriods = selectedWorkspaceId 
    ? periodsByWorkspace[selectedWorkspaceId] || []
    : periodsByWorkspace.personal || [];
  
  const currentPeriod = currentPeriods.find(p => p.id === selectedPeriodId);

  const getWorkspaceIcon = (type) => {
    switch (type) {
      case 'organisation': return Building2;
      case 'team': return Users;
      default: return User;
    }
  };

  const getPeriodStatus = (period) => {
    if (period.status === 'completed') return { label: 'Completed', color: 'gray' };
    if (period.status === 'draft') return { label: 'Draft', color: 'amber' };
    if (period.isFuture) return { label: 'Upcoming', color: 'blue' };
    if (period.isActive) return { label: `Week ${period.currentWeek}`, color: 'green' };
    return { label: period.status, color: 'gray' };
  };

  return (
    <Card className="mb-6">
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            {currentWorkspace 
              ? React.createElement(getWorkspaceIcon(currentWorkspace.type), { className: "w-5 h-5 text-indigo-600" })
              : <User className="w-5 h-5 text-indigo-600" />
            }
          </div>
          <div>
            <div className="font-medium text-gray-900">
              {currentWorkspace?.name || 'Personal Goals'}
            </div>
            {currentPeriod ? (
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <span>{currentPeriod.name}</span>
                <Badge color={getPeriodStatus(currentPeriod).color} size="sm">
                  {getPeriodStatus(currentPeriod).label}
                </Badge>
              </div>
            ) : (
              <div className="text-sm text-amber-600">No active period</div>
            )}
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          {/* Workspace Tabs */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => onSelectWorkspace(null)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                !selectedWorkspaceId
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <User className="w-4 h-4" />
              Personal
            </button>
            
            {workspaces.map(ws => {
              const WsIcon = getWorkspaceIcon(ws.type);
              const wsData = periodsByWorkspace[ws.id];
              const hasActive = wsData?.some(p => p.status === 'active');
              
              return (
                <button
                  key={ws.id}
                  onClick={() => onSelectWorkspace(ws.id)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    selectedWorkspaceId === ws.id
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <WsIcon className="w-4 h-4" />
                  {ws.name}
                  {hasActive && <span className="w-2 h-2 bg-green-500 rounded-full" />}
                </button>
              );
            })}
          </div>

          {/* Periods for Selected Workspace */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Periods</span>
              <Button size="sm" variant="secondary" onClick={() => onCreatePeriod(selectedWorkspaceId)}>
                <Plus className="w-4 h-4 mr-1" /> New Period
              </Button>
            </div>

            {currentPeriods.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 rounded-lg">
                <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No periods yet</p>
                <Button 
                  size="sm" 
                  className="mt-2"
                  onClick={() => onCreatePeriod(selectedWorkspaceId)}
                >
                  Create First Period
                </Button>
              </div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {currentPeriods.map(period => {
                  const status = getPeriodStatus(period);
                  const isSelected = period.id === selectedPeriodId;
                  
                  return (
                    <button
                      key={period.id}
                      onClick={() => onSelectPeriod(period.id)}
                      className={`w-full p-3 rounded-lg text-left transition-all ${
                        isSelected
                          ? 'bg-indigo-50 border-2 border-indigo-500'
                          : 'bg-gray-50 border-2 border-transparent hover:border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">{period.name}</span>
                        <Badge color={status.color} size="sm">{status.label}</Badge>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(period.startDate).toLocaleDateString()} — {new Date(period.endDate).toLocaleDateString()}
                        {period.goalIds?.length > 0 && (
                          <span className="ml-2">• {period.goalIds.length} goals</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

// --- PERIOD CREATE MODAL ---
const PeriodCreateModal = ({ workspaceId, workspaces = [], existingPeriods = [], onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    periodVision: '',
    startDate: getNextPeriodStartDate(),
    workspaceId: workspaceId || null
  });
  const [saving, setSaving] = useState(false);

  const selectedWorkspace = workspaces.find(w => w.id === formData.workspaceId);
  const calculatedEndDate = getPeriodEndDate(formData.startDate);
  
  // Check for overlapping periods
  const startDate = new Date(formData.startDate);
  const endDate = new Date(calculatedEndDate);
  const hasOverlap = existingPeriods
    .filter(p => p.workspaceId === formData.workspaceId)
    .some(p => {
      const pStart = new Date(p.startDate);
      const pEnd = new Date(p.endDate);
      return (startDate <= pEnd && endDate >= pStart);
    });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isFuture = startDate > today;

  const handleSave = async () => {
    if (hasOverlap) return;
    
    setSaving(true);
    try {
      await onSave?.({
        ...formData,
        endDate: calculatedEndDate,
        status: isFuture ? 'draft' : 'active'
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  // Generate suggested name
  const generateName = () => {
    const start = new Date(formData.startDate);
    const quarter = Math.ceil((start.getMonth() + 1) / 3);
    const prefix = selectedWorkspace?.name || 'Personal';
    return `${prefix} Q${quarter} ${start.getFullYear()}`;
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold">Create 12-Week Period</h3>
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-lg">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        {/* Workspace */}
        <WorkspaceSelector
          value={formData.workspaceId}
          onChange={val => setFormData(prev => ({ ...prev, workspaceId: val }))}
          workspaces={workspaces}
          label="Period Workspace"
        />

        {/* Period Name */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">Period Name</label>
            <button 
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, name: generateName() }))}
              className="text-xs text-indigo-600 hover:text-indigo-800"
            >
              Auto-generate
            </button>
          </div>
          <Input
            value={formData.name}
            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Q1 2025 Sprint"
          />
        </div>

        {/* Vision */}
        <TextArea
          label="Period Vision (Optional)"
          value={formData.periodVision}
          onChange={e => setFormData(prev => ({ ...prev, periodVision: e.target.value }))}
          placeholder="What does success look like at the end of these 12 weeks?"
          rows={2}
        />

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={formatDateForInput(formData.startDate)}
              onChange={e => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-700">
              {new Date(calculatedEndDate).toLocaleDateString()}
            </div>
            <p className="text-xs text-gray-500 mt-1">12 weeks from start</p>
          </div>
        </div>

        {/* Status Preview */}
        <div className={`p-3 rounded-lg flex items-start gap-3 ${
          isFuture ? 'bg-blue-50 border border-blue-200' : 'bg-green-50 border border-green-200'
        }`}>
          {isFuture ? (
            <>
              <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Future Period (Draft)</p>
                <p className="text-sm text-blue-700">
                  This period starts in the future. It will be created as a draft. 
                  You can add goals now and activate them when the period begins.
                </p>
              </div>
            </>
          ) : (
            <>
              <Play className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-900">Active Period</p>
                <p className="text-sm text-green-700">
                  This period starts today or in the past. It will be created as active.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Overlap Warning */}
        {hasOverlap && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-900">Date Conflict</p>
              <p className="text-sm text-red-700">
                This period overlaps with an existing period in the same workspace.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving || hasOverlap || !formData.name}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          Create {isFuture ? 'Draft' : 'Active'} Period
        </Button>
      </div>
    </Card>
  );
};

export const DiaryGoalsView = ({
  bookId,
  customStyling = {},
  workspaces = [],
  workspaceMembers = [],
  goalService: gs,
  // Handlers from parent
  onCreatePeriod,
  onUpdatePeriod,
  onCompletePeriod,
  onCreateGoal,
  onUpdateGoal,
  onDeleteGoal,
  onSaveWeeklyScore
}) => {
  // View state
  const [view, setView] = useState('dashboard'); // dashboard, form, scorecard, period-edit, period-create
  
  // Multi-workspace state
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [periodsByWorkspace, setPeriodsByWorkspace] = useState({});
  const [multiDashboard, setMultiDashboard] = useState(null);
  
  // Current context
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [currentGoals, setCurrentGoals] = useState([]);
  
  // Edit state
  const [editingGoal, setEditingGoal] = useState(null);
  const [scoringGoal, setScoringGoal] = useState(null);
  
  const [loading, setLoading] = useState(true);

  // Load all data
  useEffect(() => {
    loadAllData();
  }, [bookId]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Get periods grouped by workspace
      const periods = await gs?.getPeriodsByWorkspace(bookId, workspaces);
      setPeriodsByWorkspace(periods || { personal: [] });
      
      // Get multi-workspace dashboard
      const dashboard = await gs?.getMultiWorkspaceDashboard(bookId, workspaces);
      setMultiDashboard(dashboard);
      
      // Set initial selection to personal active period
      const personalActive = periods?.personal?.find(p => p.status === 'active');
      if (personalActive && !selectedPeriodId) {
        setSelectedPeriodId(personalActive.id);
      }
    } catch (error) {
      console.error('Error loading goals data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load current period and goals when selection changes
  useEffect(() => {
    loadCurrentPeriodData();
  }, [selectedPeriodId, selectedWorkspaceId]);

  const loadCurrentPeriodData = async () => {
    if (!selectedPeriodId) {
      setCurrentPeriod(null);
      setCurrentGoals([]);
      return;
    }
    
    try {
      const period = await gs?.getPeriod(bookId, selectedPeriodId);
      setCurrentPeriod(period);
      
      if (period) {
        const goals = await gs?.getGoals(bookId, { periodId: period.id });
        setCurrentGoals(goals || []);
      }
    } catch (error) {
      console.error('Error loading period data:', error);
    }
  };

  // Get current periods for selected workspace
  const currentPeriods = selectedWorkspaceId 
    ? periodsByWorkspace[selectedWorkspaceId] || []
    : periodsByWorkspace.personal || [];

  const currentWeek = currentPeriod ? getCurrentWeekNumber(currentPeriod.startDate) : 1;

  // Handlers
  const handleSelectWorkspace = (workspaceId) => {
    setSelectedWorkspaceId(workspaceId);
    // Auto-select active period for this workspace
    const periods = workspaceId 
      ? periodsByWorkspace[workspaceId] || []
      : periodsByWorkspace.personal || [];
    const activePeriod = periods.find(p => p.status === 'active');
    setSelectedPeriodId(activePeriod?.id || periods[0]?.id || null);
  };

  const handleSelectPeriod = (periodId) => {
    setSelectedPeriodId(periodId);
    setView('dashboard');
  };

  const handleCreatePeriodClick = (workspaceId) => {
    setSelectedWorkspaceId(workspaceId);
    setView('period-create');
  };

  const handleSavePeriod = async (periodData) => {
    try {
      const enrichedPeriodData = {
        ...periodData,
        workspaceId: selectedWorkspaceId ?? null
      };

      if (onCreatePeriod) {
        await onCreatePeriod(enrichedPeriodData);
      } else if (gs) {
        await gs.createFuturePeriod(bookId, enrichedPeriodData);
      }

      await loadAllData();
      setView('dashboard');
    } catch (error) {
      console.error('Error creating period:', error);
    }
  };

  const handleActivatePeriod = async () => {
    if (!currentPeriod?.id) return;
    
    try {
      await gs?.activatePeriod(bookId, currentPeriod.id);
      await loadAllData();
      await loadCurrentPeriodData();
    } catch (error) {
      console.error('Error activating period:', error);
    }
  };

  const handleSaveGoal = async (goalData) => {
    try {
      const data = {
        ...goalData,
        workspaceId: selectedWorkspaceId || null
      };
      
      if (editingGoal?.id) {
        if (onUpdateGoal) {
          await onUpdateGoal(editingGoal.id, data);
        } else if (gs) {
          await gs.updateGoal(bookId, editingGoal.id, data);
        }
      } else {
        if (onCreateGoal) {
          await onCreateGoal(data);
        } else if (gs) {
          await gs.createGoal(bookId, data, currentPeriod?.id);
        }
      }
      
      setView('dashboard');
      setEditingGoal(null);
      await loadCurrentPeriodData();
      await loadAllData();
    } catch (error) {
      console.error('Error saving goal:', error);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Delete this goal and all its scores?')) return;
    try {
      if (onDeleteGoal) {
        await onDeleteGoal(goalId);
      } else if (gs) {
        await gs.deleteGoal(bookId, goalId);
      }
      await loadCurrentPeriodData();
      await loadAllData();
    } catch (error) {
      console.error('Error deleting goal:', error);
    }
  };

  const handleSaveScore = async (scoreData) => {
    try {
      if (onSaveWeeklyScore) {
        await onSaveWeeklyScore(scoringGoal.id, currentWeek, scoreData);
      } else if (gs) {
        await gs.saveWeeklyScore(bookId, scoringGoal.id, currentWeek, scoreData);
      }
      setView('dashboard');
      setScoringGoal(null);
      await loadCurrentPeriodData();
    } catch (error) {
      console.error('Error saving score:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-3 py-4 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Target className="w-6 h-6 text-indigo-600" />
              Goals
            </h1>
            <p className="text-sm text-gray-500">12-Week Year execution system</p>
          </div>
          
          {view === 'dashboard' && currentPeriod && (
            <div className="flex items-center gap-2">
              {currentPeriod.status === 'draft' && (
                <Button variant="secondary" onClick={handleActivatePeriod}>
                  <Play className="w-4 h-4 mr-2" /> Activate Period
                </Button>
              )}
              <Button 
                onClick={() => { setEditingGoal(null); setView('form'); }}
                style={{ backgroundColor: customStyling.primaryColor }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Goal
              </Button>
            </div>
          )}
          
          {view !== 'dashboard' && (
            <Button variant="secondary" onClick={() => { 
              setView('dashboard'); 
              setEditingGoal(null); 
              setScoringGoal(null); 
            }}>
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3">
        <div className="max-w-5xl mx-auto">
          
          {/* Workspace & Period Selector - Always visible */}
          {view === 'dashboard' && (
            <WorkspacePeriodSelector
              workspaces={workspaces}
              periodsByWorkspace={periodsByWorkspace}
              selectedWorkspaceId={selectedWorkspaceId}
              selectedPeriodId={selectedPeriodId}
              onSelectWorkspace={handleSelectWorkspace}
              onSelectPeriod={handleSelectPeriod}
              onCreatePeriod={handleCreatePeriodClick}
            />
          )}

          {/* Dashboard View */}
          {view === 'dashboard' && (
            <div className="space-y-6">
              {currentPeriod ? (
                <>
                  <PeriodHeader 
                    period={currentPeriod} 
                    goals={currentGoals}
                    onEditPeriod={() => setView('period-edit')}
                  />
                  
                  {currentGoals.length === 0 ? (
                    <Card className="p-8 text-center">
                      <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-700 mb-2">No Goals Yet</h3>
                      <p className="text-gray-500 mb-4">
                        {currentPeriod.status === 'draft' 
                          ? 'Add goals now and they\'ll be ready when you activate this period.'
                          : 'The 12-Week Year works best with 2-3 focused goals.'}
                      </p>
                      <Button onClick={() => setView('form')}>
                        <Plus className="w-4 h-4 mr-2" /> Create Your First Goal
                      </Button>
                    </Card>
                  ) : (
                    <div className="grid gap-6">
                      {currentGoals.map(goal => (
                        <GoalCard
                          key={goal.id}
                          goal={goal}
                          currentWeek={currentWeek}
                          periodStatus={currentPeriod.status}
                          onEdit={g => { setEditingGoal(g); setView('form'); }}
                          onDelete={handleDeleteGoal}
                          onScore={g => { setScoringGoal(g); setView('scorecard'); }}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Card className="p-4 text-center bg-gradient-to-br from-indigo-50 to-purple-50">
                  <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8 text-indigo-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {selectedWorkspaceId 
                      ? `No Period for ${workspaces.find(w => w.id === selectedWorkspaceId)?.name}`
                      : 'No Personal Period'}
                  </h2>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Create a 12-week period to start tracking goals.
                  </p>
                  <Button onClick={() => setView('period-create')}>
                    <Plus className="w-4 h-4 mr-2" /> Create Period
                  </Button>
                </Card>
              )}
            </div>
          )}

          {/* Period Create View */}
          {view === 'period-create' && (
            <PeriodCreateModal
              workspaceId={selectedWorkspaceId}
              workspaces={workspaces}
              existingPeriods={Object.values(periodsByWorkspace).flat()}
              onSave={handleSavePeriod}
              onCancel={() => setView('dashboard')}
            />
          )}

          {/* Period Edit View */}
          {view === 'period-edit' && currentPeriod && (
            <PeriodEditor
              period={currentPeriod}
              workspaces={workspaces}
              onSave={async (updates) => {
                if (onUpdatePeriod) {
                  await onUpdatePeriod(currentPeriod.id, updates);
                } else if (gs) {
                  await gs.updatePeriod(bookId, currentPeriod.id, updates);
                }
                await loadAllData();
                await loadCurrentPeriodData();
                setView('dashboard');
              }}
              onCancel={() => setView('dashboard')}
              onComplete={async () => {
                if (onCompletePeriod) {
                  await onCompletePeriod(currentPeriod.id);
                } else if (gs) {
                  await gs.completePeriod(bookId, currentPeriod.id);
                }
                await loadAllData();
                setSelectedPeriodId(null);
                setView('dashboard');
              }}
            />
          )}

          {/* Goal Form View */}
          {view === 'form' && (
            <GoalForm
              goal={editingGoal}
              period={currentPeriod}
              workspaces={workspaces}
              workspaceMembers={workspaceMembers}
              defaultWorkspaceId={selectedWorkspaceId}
              onSave={handleSaveGoal}
              onCancel={() => { setView('dashboard'); setEditingGoal(null); }}
            />
          )}

          {/* Scorecard View */}
          {view === 'scorecard' && scoringGoal && (
            <WeeklyScorecard
              goal={scoringGoal}
              weekNumber={currentWeek}
              periodStatus={currentPeriod?.status}
              onSave={handleSaveScore}
              onCancel={() => { setView('dashboard'); setScoringGoal(null); }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DiaryGoalsView;